import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTasks, faChevronDown, faChevronUp, faLightbulb } from '@fortawesome/free-solid-svg-icons';

/**
 * Simplified Evaluation Drawer Component
 */
const EvaluationDrawer = ({
  isOpen,
  onClose,
  evaluation,
  onSendQuestionToChat,
  onAddTodoItem
}) => {
  const [expandedCategories, setExpandedCategories] = useState([]);
  
  // Toggle a category's expanded state
  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => 
      prev.includes(categoryName)
        ? prev.filter(name => name !== categoryName)
        : [...prev, categoryName]
    );
  };
  
  // Check if a category is expanded
  const isCategoryExpanded = (categoryName) => {
    return expandedCategories.includes(categoryName);
  };
  
  // Add a suggestion to the todo list
  const handleAddToTodo = (suggestion, source = 'evaluation') => {
    if (onAddTodoItem) {
      // Simply create a new todo item with the suggestion content
      onAddTodoItem({
        content: suggestion,
        type: 'suggestion',
        source
      });
    }
  };

  if (!evaluation) return null;

  return (
    <div
      className={`evaluation-drawer ${isOpen ? 'open' : 'closed'}`}
      style={{
        height: '100vh',
        borderRight: '1px solid #ddd',
        backgroundColor: '#fff',
        position: 'fixed',
        top: 0,
        right: 0,
        overflowY: 'auto',
        zIndex: 1050,
        transition: 'transform 0.3s ease-in-out',
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        width: '500px',
      }}
    >
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Screenplay Evaluation</h4>
          <button
            className="btn-close"
            onClick={onClose}
            aria-label="Close Evaluation"
          ></button>
        </div>
        
        {/* Executive Summary */}
        <div className="card mb-4 shadow-sm">
          <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Executive Summary</h5>
            {evaluation.executiveSummary && (
              <span className="badge bg-light text-dark">
                Rating: {evaluation.executiveSummary.overallRating}/10
              </span>
            )}
          </div>
          <div className="card-body">
            <p>{evaluation.executiveSummary?.summary || 'No summary available.'}</p>
          </div>
        </div>
        
        {/* Categories */}
        {evaluation.categories?.map((category, idx) => (
          <div key={idx} className="card mb-3 shadow-sm">
            <div 
              className="card-header d-flex justify-content-between align-items-center"
              style={{ 
                backgroundColor: getCategoryColor(category.rating),
                color: category.rating > 6 ? '#000' : '#fff',
                cursor: 'pointer'
              }}
              onClick={() => toggleCategory(category.name)}
            >
              <h5 className="mb-0">{category.name}</h5>
              <div className="d-flex align-items-center">
                <span className="badge bg-light text-dark me-2">
                  {category.rating}/10
                </span>
                <FontAwesomeIcon 
                  icon={isCategoryExpanded(category.name) ? faChevronUp : faChevronDown} 
                />
              </div>
            </div>
            
            {isCategoryExpanded(category.name) && (
              <div className="card-body">
                <div className="mb-3">
                  <h6>Analysis:</h6>
                  <p>{category.explanation}</p>
                </div>
                
                {/* Suggestions */}
                {category.suggestions?.length > 0 && (
                  <div className="mb-3">
                    <h6>Suggestions:</h6>
                    <div className="list-group">
                      {category.suggestions.map((suggestion, suggIdx) => (
                        <div key={suggIdx} className="list-group-item">
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="me-2">
                              <span className={`badge ${getBadgeColor(suggestion.target)} me-2`}>
                                {formatTarget(suggestion.target)}
                              </span>
                              {suggestion.specificTarget !== 'none' && (
                                <span className="badge bg-secondary me-2">
                                  {suggestion.specificTarget}
                                </span>
                              )}
                            </div>
                            <div className="btn-group">
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => handleAddToTodo(suggestion.suggestion)}
                                title="Add to Todo"
                              >
                                <FontAwesomeIcon icon={faTasks} /> Add as Todo
                              </button>
                            </div>
                          </div>
                          <p className="mb-0 mt-1">{suggestion.suggestion}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Questions */}
                {category.questions?.length > 0 && (
                  <div>
                    <h6>Questions to Explore:</h6>
                    <ul className="list-group">
                      {category.questions.map((question, qIdx) => (
                        <li key={qIdx} className="list-group-item d-flex justify-content-between align-items-center">
                          <span>{question}</span>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => onSendQuestionToChat(question)}
                          >
                            Ask in Chat
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        
        {/* Workspace-level questions */}
        {evaluation.questions?.length > 0 && (
          <div className="card mb-4 shadow-sm">
            <div className="card-header bg-info text-white">
              <h5 className="mb-0">General Questions</h5>
            </div>
            <div className="card-body">
              <ul className="list-group">
                {evaluation.questions.map((question, idx) => (
                  <li key={idx} className="list-group-item d-flex justify-content-between align-items-center">
                    <span>{question}</span>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => onSendQuestionToChat(question)}
                    >
                      Ask in Chat
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function to get color based on rating
const getCategoryColor = (rating) => {
  if (rating >= 8) return '#4caf50'; // Green
  if (rating >= 6) return '#8bc34a'; // Light Green
  if (rating >= 4) return '#ffc107'; // Yellow
  if (rating >= 2) return '#ff9800'; // Orange
  return '#f44336'; // Red
};

// Helper function to get badge color based on target
const getBadgeColor = (target) => {
  switch (target) {
    case 'theme':
      return 'bg-info';
    case 'characters':
      return 'bg-success';
    case 'scenes':
    case 'plot_items':
      return 'bg-primary';
    default:
      return 'bg-secondary';
  }
};

// Helper function to format target name
const formatTarget = (target) => {
  switch (target) {
    case 'theme':
      return 'Theme';
    case 'characters':
      return 'Character';
    case 'scenes':
    case 'plot_items':
      return 'Plot Item';
    default:
      return 'General';
  }
};

EvaluationDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  evaluation: PropTypes.object,
  onSendQuestionToChat: PropTypes.func,
  onAddTodoItem: PropTypes.func
};

export default EvaluationDrawer;